/**
 * useAddressSearch - Hook para búsqueda de direcciones con CartoCiudad
 * 
 * V17 - PTEL Visor Cartográfico
 * 
 * Características:
 * - Debounce configurable
 * - Autocompletado con candidatos
 * - Conversión automática a UTM30
 * - Caché de búsquedas
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { CartoCiudadGeocoder } from '../../../services/geocoding/generic/CartoCiudadGeocoder';

// =============================================================================
// TIPOS
// =============================================================================

export interface AddressCandidate {
  /** ID único del candidato */
  id: string;
  /** Dirección completa formateada */
  address: string;
  /** Tipo: portal, callejero, municipio */
  type: 'portal' | 'callejero' | 'municipio' | 'provincia';
  /** Municipio */
  municipality?: string;
  /** Provincia */
  province?: string;
  /** Coordenadas UTM30 si disponibles */
  coords?: { x: number; y: number };
}

export interface AddressSearchResult {
  /** Dirección encontrada */
  address: string;
  /** Coordenadas UTM30 ETRS89 */
  x: number;
  y: number;
  /** Confianza del resultado (0-100) */
  confidence: number;
  /** Tipo de match */
  matchType: string;
  /** Municipio */
  municipality?: string;
  /** Provincia */
  province?: string;
}

export interface UseAddressSearchOptions {
  /** Tiempo de debounce en ms (default: 300) */
  debounceMs?: number;
  /** Municipio para filtrar resultados */
  municipality?: string;
  /** Provincia para filtrar resultados */
  province?: string;
  /** Máximo de candidatos a mostrar (default: 5) */
  maxCandidates?: number;
  /** Callback cuando se selecciona una dirección */
  onSelect?: (result: AddressSearchResult) => void;
}

export interface UseAddressSearchReturn {
  /** Término de búsqueda actual */
  query: string;
  /** Actualizar término de búsqueda */
  setQuery: (query: string) => void;
  /** Lista de candidatos */
  candidates: AddressCandidate[];
  /** Estado de carga */
  isLoading: boolean;
  /** Error si existe */
  error: string | null;
  /** Resultado seleccionado */
  selectedResult: AddressSearchResult | null;
  /** Seleccionar un candidato */
  selectCandidate: (candidate: AddressCandidate) => Promise<void>;
  /** Limpiar búsqueda */
  clear: () => void;
  /** Buscar manualmente (sin debounce) */
  search: (query: string) => Promise<void>;
}

// =============================================================================
// HOOK
// =============================================================================

export function useAddressSearch(options: UseAddressSearchOptions = {}): UseAddressSearchReturn {
  const {
    debounceMs = 300,
    municipality,
    province,
    maxCandidates = 5,
    onSelect,
  } = options;

  // Estado
  const [query, setQueryState] = useState('');
  const [candidates, setCandidates] = useState<AddressCandidate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedResult, setSelectedResult] = useState<AddressSearchResult | null>(null);

  // Refs
  const geocoderRef = useRef<CartoCiudadGeocoder | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Inicializar geocoder
  useEffect(() => {
    geocoderRef.current = new CartoCiudadGeocoder();
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  /**
   * Buscar candidatos
   */
  const searchCandidates = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim() || searchQuery.length < 3) {
      setCandidates([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    // Cancelar búsqueda anterior
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      const geocoder = geocoderRef.current;
      if (!geocoder) return;

      // Usar el endpoint de candidates directamente
      const response = await fetch(
        `https://www.cartociudad.es/geocoder/api/geocoder/candidates?` +
        new URLSearchParams({
          q: municipality ? `${searchQuery}, ${municipality}` : searchQuery,
          limit: String(maxCandidates),
          countrycodes: 'es',
          ...(province && { provincia: province }),
        }),
        { signal: abortControllerRef.current.signal }
      );

      if (!response.ok) {
        throw new Error(`Error ${response.status}`);
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        setCandidates([]);
        return;
      }

      // Filtrar por municipio si se especificó
      let filteredData = data;
      if (municipality) {
        const searchMuni = municipality.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        filteredData = data.filter((c: Record<string, unknown>) => {
          const candidateMuni = ((c.muni as string) || (c.locality as string) || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          return candidateMuni.includes(searchMuni) || searchMuni.includes(candidateMuni);
        });
      }

      // Mapear a AddressCandidate
      const mappedCandidates: AddressCandidate[] = filteredData
        .slice(0, maxCandidates)
        .map((c: Record<string, unknown>, index: number) => ({
          id: (c.id as string) || `candidate-${index}`,
          address: formatCandidateAddress(c),
          type: (c.type as AddressCandidate['type']) || 'callejero',
          municipality: (c.muni as string) || (c.locality as string),
          province: c.province as string,
        }));

      setCandidates(mappedCandidates);

    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        return; // Ignorar cancelaciones
      }
      console.error('Error buscando candidatos:', err);
      setError('Error al buscar direcciones');
      setCandidates([]);
    } finally {
      setIsLoading(false);
    }
  }, [municipality, province, maxCandidates]);

  /**
   * Actualizar query con debounce
   */
  const setQuery = useCallback((newQuery: string) => {
    setQueryState(newQuery);
    setSelectedResult(null);

    // Limpiar timer anterior
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Nueva búsqueda con debounce
    debounceTimerRef.current = setTimeout(() => {
      searchCandidates(newQuery);
    }, debounceMs);
  }, [debounceMs, searchCandidates]);

  /**
   * Búsqueda manual sin debounce
   */
  const search = useCallback(async (searchQuery: string) => {
    setQueryState(searchQuery);
    await searchCandidates(searchQuery);
  }, [searchCandidates]);

  /**
   * Seleccionar candidato y geocodificar
   */
  const selectCandidate = useCallback(async (candidate: AddressCandidate) => {
    setIsLoading(true);
    setError(null);

    try {
      const geocoder = geocoderRef.current;
      if (!geocoder) {
        throw new Error('Geocoder no inicializado');
      }

      // Geocodificar la dirección seleccionada
      const result = await geocoder.geocode({
        address: candidate.address,
        municipality: candidate.municipality || municipality,
        province: candidate.province || province,
      });

      if (!result.success) {
        throw new Error('No se pudo geocodificar la dirección');
      }

      const searchResult: AddressSearchResult = {
        address: candidate.address,
        x: result.data.x,
        y: result.data.y,
        confidence: result.data.confidence,
        matchType: result.data.matchType || 'unknown',
        municipality: result.data.municipality,
        province: result.data.province,
      };

      setSelectedResult(searchResult);
      setCandidates([]);
      onSelect?.(searchResult);

    } catch (err) {
      console.error('Error geocodificando candidato:', err);
      setError('Error al obtener coordenadas');
    } finally {
      setIsLoading(false);
    }
  }, [municipality, province, onSelect]);

  /**
   * Limpiar búsqueda
   */
  const clear = useCallback(() => {
    setQueryState('');
    setCandidates([]);
    setSelectedResult(null);
    setError(null);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
  }, []);

  return {
    query,
    setQuery,
    candidates,
    isLoading,
    error,
    selectedResult,
    selectCandidate,
    clear,
    search,
  };
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Formatea la dirección de un candidato para mostrar
 */
function formatCandidateAddress(candidate: Record<string, unknown>): string {
  const parts: string[] = [];
  
  // Tipo de vía + nombre
  if (candidate.tip_via) parts.push(candidate.tip_via as string);
  if (candidate.address) parts.push(candidate.address as string);
  if (candidate.portalNumber) parts.push(String(candidate.portalNumber));
  
  // Municipio
  const muni = (candidate.muni as string) || (candidate.locality as string);
  if (muni) parts.push(muni);
  
  return parts.join(' ').trim() || 'Dirección desconocida';
}

export default useAddressSearch;
